import { API_CONFIG } from "@/lib/api/config";

export const META_MAX_VIDEO_BYTES = 4 * 1024 * 1024 * 1024;

export interface AdVideoUploadResult {
  url: string;
  fallbackUrl: string;
  key: string;
}

export interface AdVideoUploadOptions {
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

interface PresignResponseBody {
  success: boolean;
  presignedUrl?: string;
  fileUrl?: string;
  fallbackUrl?: string;
  key?: string;
  expiresIn?: number;
  error?: string;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
}

export function validateAdVideo(file: File | Blob | null | undefined): string | null {
  if (!file || file.size === 0) return "Empty file — nothing to upload";
  if (!file.type || !file.type.startsWith("video/")) {
    return "That file is not a video. Upload an MP4 or MOV.";
  }
  if (file.size > META_MAX_VIDEO_BYTES) {
    return `Video is ${formatBytes(file.size)} — Meta's limit is ${formatBytes(
      META_MAX_VIDEO_BYTES,
    )}. Please compress it and try again.`;
  }
  return null;
}

function putToS3(
  presignedUrl: string,
  file: File | Blob,
  fileType: string,
  opts: AdVideoUploadOptions,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presignedUrl, true);
    xhr.setRequestHeader("Content-Type", fileType);

    const onAbort = () => xhr.abort();
    if (opts.signal) {
      if (opts.signal.aborted) {
        reject(new Error("Upload cancelled"));
        return;
      }
      opts.signal.addEventListener("abort", onAbort, { once: true });
    }
    const cleanup = () => opts.signal?.removeEventListener("abort", onAbort);

    if (opts.onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) {
          opts.onProgress?.(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      if (xhr.status === 403) {
        reject(new Error("Upload rejected by S3 — the upload link expired. Please try again."));
        return;
      }
      if (xhr.status >= 500) {
        reject(new Error(`S3 is currently unavailable (${xhr.status}). Please retry.`));
        return;
      }
      reject(new Error(`Upload failed (${xhr.status} ${xhr.statusText})`));
    };

    xhr.onerror = () => {
      cleanup();
      reject(new Error("Upload failed — network error. Check your connection and retry."));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new Error("Upload cancelled"));
    };

    xhr.send(file);
  });
}

export async function uploadAdVideoToS3(
  file: File | Blob,
  opts: AdVideoUploadOptions = {},
): Promise<AdVideoUploadResult> {
  const validationError = validateAdVideo(file);
  if (validationError) throw new Error(validationError);

  const filename = (file as File).name || "ad-video.mp4";
  const fileType = file.type || "video/mp4";

  let presignRes: Response;
  try {
    presignRes = await fetch(`${API_CONFIG.BASE_URL}/ads/upload-video/presign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      },
      body: JSON.stringify({ fileName: filename, fileType }),
      signal: opts.signal,
    });
  } catch (networkErr) {
    if (opts.signal?.aborted) throw new Error("Upload cancelled");
    const msg = networkErr instanceof Error ? networkErr.message : "unreachable";
    throw new Error(`Failed to request upload URL — network error: ${msg}`);
  }

  if (!presignRes.ok) {
    let errorMsg = `Failed to request upload URL (${presignRes.status})`;
    try {
      const body = (await presignRes.json()) as { error?: string };
      if (body?.error) errorMsg = body.error;
    } catch {
    }
    throw new Error(errorMsg);
  }

  const presignBody = (await presignRes.json()) as PresignResponseBody;
  if (!presignBody.success || !presignBody.presignedUrl || !presignBody.fileUrl) {
    throw new Error(presignBody.error || "Invalid presigned-URL response from server");
  }

  await putToS3(presignBody.presignedUrl, file, fileType, opts);

  return {
    url: presignBody.fileUrl,
    fallbackUrl: presignBody.fallbackUrl || presignBody.fileUrl,
    key: presignBody.key || "",
  };
}
